/**
 * Single-flight re-entrancy guards — one canonical home for the double-submit pattern:
 * drop the call while one invocation is already in flight, and clear the flag in
 * .finally() so it resets on success and error alike.
 *
 * The flag flips synchronously before the first await, and the wrapped function is
 * invoked synchronously so event handlers can still use live event objects. A same-tick
 * double-fire (fast double-click / re-entrant trigger) is collapsed to a single call.
 * The wrapped function's returned value is passed through (the guard is awaitable), and
 * rejections are NOT swallowed — they propagate so Sentry's unhandled-rejection
 * integration can see genuine failures. Expected/handled errors should be dealt with
 * inside the wrapped function (the Redux `crud` thunks resolve rather than reject, so
 * they never surface here).
 */

/**
 * Wrap `fn` so overlapping invocations are dropped while one is in flight.
 * Returns the in-flight promise (or `undefined` when a call is dropped).
 */
export const makeSingleFlight = (fn) => {
  let inFlight = false;
  return (...args) => {
    if (inFlight) return undefined;
    inFlight = true;
    let result;
    try {
      result = fn(...args);
    } catch (error) {
      inFlight = false;
      return Promise.reject(error);
    }
    return Promise.resolve(result).finally(() => {
      inFlight = false;
    });
  };
};

/**
 * Keyed variant: only the duplicate in-flight intent for the same key is dropped, so
 * concurrent calls for different keys still proceed. `keyOf` derives the key from the
 * call arguments (defaults to the first argument).
 */
export const makeKeyedSingleFlight = (fn, keyOf = (...args) => args[0]) => {
  const inFlight = new Set();
  return (...args) => {
    const key = keyOf(...args);
    if (inFlight.has(key)) return undefined;
    inFlight.add(key);
    let result;
    try {
      result = fn(...args);
    } catch (error) {
      inFlight.delete(key);
      return Promise.reject(error);
    }
    return Promise.resolve(result).finally(() => {
      inFlight.delete(key);
    });
  };
};
